/**
 * Project Detail → Material tab. 1:1 with the design folder's
 * `project-details.html#ptab-material` and the mobile
 * `src/features/projects/tabs/MaterialTab.tsx`.
 *
 * Layout:
 *   Filter chips (All / Pending / Approved / Rejected) + Library btn
 *   KPI tiles (Required Now · Visible Value)
 *   Material Request list — per row: title · item count · audit · ₹ · status pill
 *   "+ New Request" CTA
 *
 * Click any row → opens MaterialRequestDetailSlideOver (preview +
 * approve / reject / edit / delivery picker / delete).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { canMarkAttendance } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { MaterialRequestDetailSlideOver } from '@/components/MaterialRequestDetailSlideOver';
import { MaterialRequestFormSlideOver } from '@/components/MaterialRequestFormSlideOver';
import {
  DELIVERY_STATUSES,
  type DeliveryStatus,
  type MaterialRequest,
  type MaterialRequestItem,
  type MaterialRequestStatus,
} from '@/lib/materialRequests';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useMaterialRequests } from '@/lib/useMaterialRequests';
import type { ProjectListItem } from '@/lib/useProjects';
import type { RoleKey } from '@/lib/useProjectMembers';

const FILTERS: { key: MaterialRequestStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
}

export function MaterialTab({ project }: { project: ProjectListItem }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { org } = useFullOrganization();
  const orgId = project.orgId ?? null;
  const { role: orgRole } = useCurrentOrgRole();
  // materialRequests use canMarkAttendance per firestore.rules:671
  const canWrite = canMarkAttendance(orgRole);

  // Caller's role on the org — drives the auto-approve gate.
  // OWNER FIRST (mirrors roleForOrg/usePermissions): the owner is always
  // superAdmin even if the roles map is sparse or carries a stale entry.
  const callerRole: RoleKey = useMemo(() => {
    if (!user || !org) return 'viewer';
    if ((org.data.ownerId as string | undefined) === user.uid) return 'superAdmin';
    const roles = (org.data.roles as Record<string, RoleKey>) ?? {};
    return roles[user.uid] ?? 'viewer';
  }, [user, org]);

  const [filter, setFilter] = useState<MaterialRequestStatus | 'all'>('all');
  const { data: requests, loading } = useMaterialRequests(
    orgId,
    project.id,
    filter === 'all' ? undefined : filter,
  );

  const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState<MaterialRequest | null>(null);

  // Keep `detail` in sync with the live snapshot (status changes,
  // delivery updates, edits all flow through `requests`).
  useEffect(() => {
    if (!detail) return;
    const fresh = requests.find((r) => r.id === detail.id);
    if (fresh && fresh !== detail) setDetail(fresh);
  }, [requests, detail]);

  // KPI tiles — derived from unfiltered request set so they don't
  // change when the user flips chips. For now we use the filtered
  // set since useMaterialRequests already filters — request a second
  // unfiltered snapshot would double the read cost.
  const kpis = useMemo(() => {
    let pendingValue = 0;
    let totalValue = 0;
    for (const r of requests) {
      totalValue += r.totalValue;
      if (r.status === 'pending') pendingValue += r.totalValue;
    }
    return { pendingValue, totalValue };
  }, [requests]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 340px',
        gap: 24,
        alignItems: 'flex-start',
      }}
    >
      {/* ─── LEFT: filter chips + list ─── */}
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Filter row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`filter-chip${filter === f.key ? ' active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/materials')}
              style={{ height: 32, fontSize: 12 }}
            >
              <i className="ph ph-books" /> Library
            </button>
            {canWrite ? (
              // Hidden for roles that can't write — Firestore rule blocks it anyway
              <button
                type="button"
                className="btn-primary"
                onClick={() => setFormOpen(true)}
                style={{ height: 32, fontSize: 12 }}
              >
                <i className="ph-bold ph-plus" /> New Request
              </button>
            ) : null}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div
            className="card"
            style={{
              padding: 32,
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            Loading material requests…
          </div>
        ) : requests.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
              <i className="ph ph-list-checks" />
            </div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {filter === 'all'
                ? 'No material requests yet'
                : `No ${filter} requests`}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                maxWidth: 380,
                margin: '0 auto 16px',
              }}
            >
              Log every material your site needs here. Items can be picked
              from the Materials Library or typed freehand.
            </div>
            {canWrite ? (
              // Hidden for roles that can't write — Firestore rule blocks it anyway
              <button
                type="button"
                className="btn-primary"
                onClick={() => setFormOpen(true)}
              >
                <i className="ph-bold ph-plus" /> New material request
              </button>
            ) : null}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {requests.map((r) => (
              <RequestRow
                key={r.id}
                request={r}
                onClick={() => setDetail(r)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ─── RIGHT rail — KPIs + tips (matches Attendance tab pattern) ─── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          position: 'sticky',
          top: 16,
        }}
      >
        <KpiTile
          label="Required Now"
          value={`₹${Math.round(kpis.pendingValue).toLocaleString('en-IN')}`}
          sub="Pending approvals"
          tone="warning"
        />
        <KpiTile
          label="Visible Value"
          value={`₹${Math.round(kpis.totalValue).toLocaleString('en-IN')}`}
          sub={`${requests.length} request${requests.length === 1 ? '' : 's'}`}
        />

        <div
          className="card"
          style={{
            padding: 16,
            fontSize: 12,
            color: 'var(--text-tertiary)',
            lineHeight: 1.5,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 11,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 6,
            }}
          >
            <i className="ph ph-info" /> Tips
          </div>
          Filter chips switch the request list. Admin / Manager auto-approves
          their own requests. Click any row to approve, reject, or update
          per-item delivery status.
        </div>
      </div>

      {/* Slide-overs */}
      <MaterialRequestFormSlideOver
        open={formOpen}
        onClose={() => setFormOpen(false)}
        orgId={orgId}
        projectId={project.id}
        request={null}
        creatorRole={callerRole}
      />
      <MaterialRequestDetailSlideOver
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        request={detail}
        callerRole={callerRole}
      />
    </div>
  );
}

// ───── Row + KPI ─────

function RequestRow({
  request,
  onClick,
}: {
  request: MaterialRequest;
  onClick: () => void;
}) {
  const received = request.items.filter(
    (i) => i.deliveryStatus === 'received_at_site',
  ).length;
  return (
    <div
      className="card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick();
      }}
      style={{
        padding: 16,
        cursor: 'pointer',
        transition: 'transform 0.1s, box-shadow 0.1s',
      }}
      title={`Open "${request.title}"`}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 10,
        }}
      >
        {/* LEFT — title + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--text-primary)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {request.title}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              marginTop: 2,
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <span>
              {request.items.length} item
              {request.items.length === 1 ? '' : 's'}
            </span>
            <span>·</span>
            <span>{fmtDate(request.createdAt?.toMillis?.() ?? null)}</span>
            {request.status === 'approved' && received > 0 ? (
              <>
                <span>·</span>
                <span>
                  {received}/{request.items.length} received
                </span>
              </>
            ) : null}
          </div>
        </div>

        {/* RIGHT — status + delivery chips inline, value beneath */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 4,
            flexShrink: 0,
            maxWidth: '60%',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <span className={`mat-status-pill ${request.status}`}>
              {request.status}
            </span>
            {/* Delivery chips inline — only on approved (meaningless
                pre-approval). */}
            {request.status === 'approved' ? (
              <DeliveryBreakdown items={request.items} />
            ) : null}
          </div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
            }}
          >
            ₹{Math.round(request.totalValue).toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      {/* Bottom audit-only row — only renders when there's something to say */}
      {request.autoApproved ||
      (request.status === 'rejected' && request.rejectionNote) ? (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            borderTop: '1px dashed var(--border)',
            paddingTop: 8,
            marginTop: request.status === 'approved' ? 8 : 0,
          }}
        >
          {request.autoApproved ? (
            <>
              <i className="ph ph-lightning" /> Auto-approved
            </>
          ) : (
            <span
              style={{ color: 'var(--critical-text)' }}
              title={request.rejectionNote}
            >
              <i className="ph ph-warning-circle" /> {request.rejectionNote}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

function KpiTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'warning';
}) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color:
            tone === 'warning' ? 'var(--warning-text)' : 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--text-tertiary)',
          marginTop: 4,
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ───── Delivery breakdown chips (visible on approved request rows) ─────

function DeliveryBreakdown({ items }: { items: MaterialRequestItem[] }) {
  // Count items per delivery status.
  const counts: Record<DeliveryStatus, number> = {
    pending: 0,
    ordered: 0,
    delivered: 0,
    received_at_site: 0,
  };
  for (const it of items) {
    counts[it.deliveryStatus] = (counts[it.deliveryStatus] ?? 0) + 1;
  }
  const total = items.length;
  const allReceived = counts.received_at_site === total && total > 0;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
      }}
    >
      {/* Per-status count chips — same visual weight as the
          APPROVED status pill, sitting inline next to it. Only
          non-zero buckets render so the row stays tight. */}
      {DELIVERY_STATUSES.map((s) =>
        counts[s.key] > 0 ? (
          <span
            key={s.key}
            className={`delivery-pill ${s.key}`}
            title={s.label}
          >
            {counts[s.key]} {s.label}
          </span>
        ) : null,
      )}
      {allReceived ? (
        <span
          className="delivery-pill received_at_site"
          style={{ fontWeight: 700 }}
          title="All items received at site"
        >
          <i className="ph-bold ph-check-circle" /> Complete
        </span>
      ) : null}
    </div>
  );
}
